
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { BotConfig, PromptRevision } from '../../types';
import { Bot, Edit3, Save, History, RotateCcw, CheckCircle } from 'lucide-react';

const BotConfigPage: React.FC = () => {
    const { allData, currentUser, updateBotConfig } = useAppContext();
    const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
    const [currentPrompt, setCurrentPrompt] = useState<string>('');
    const [isDirty, setIsDirty] = useState(false);
    // activeRevisionId state removed as it was unused and redundant


    // Load initial state when bot selection changes
    useEffect(() => {
        if (selectedBotId) {
            const bot = allData.botConfigs.find(b => b.id === selectedBotId);
            if (bot) {
                const activeRev = bot.revisions.find(r => r.id === bot.activeRevisionId);
                setCurrentPrompt(activeRev?.content || bot.role); // Fallback to role if revision missing
                setIsDirty(false);
            }
        }
    }, [selectedBotId, allData.botConfigs]);

    const selectedBot = allData.botConfigs.find(b => b.id === selectedBotId);

    const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCurrentPrompt(e.target.value);
        setIsDirty(true);
    };

    const handleSaveRevision = async () => {
        if (!selectedBot || !currentUser) return;

        const newRevisionId = `REV-${Date.now()}`;
        const newVersion = selectedBot.revisions.length + 1;

        const newRevision: PromptRevision = {
            id: newRevisionId,
            content: currentPrompt,
            timestamp: new Date().toISOString(),
            author: currentUser.username,
            version: newVersion,
            notes: `Updated via Admin Console`
        };

        const updatedBot: BotConfig = {
            ...selectedBot,
            activeRevisionId: newRevisionId,
            role: currentPrompt, // Update the main role field too for immediate use
            revisions: [newRevision, ...selectedBot.revisions] // Add newest first
        };

        await updateBotConfig(updatedBot);
        setIsDirty(false);
        alert(`Saved Revision v${newVersion}`);
    };

    const handleRestoreRevision = (rev: PromptRevision) => {
        if (window.confirm(`Revert prompt to version ${rev.version}? Unsaved changes will be lost.`)) {
            setCurrentPrompt(rev.content);
            // activeRevisionId update removed
            setIsDirty(true); // Mark as dirty so user has to "Save" to commit the revert
        }
    };

    return (
        <div className="h-full flex flex-col gap-6">
            <div>
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                    <Bot className="text-primary" /> AI Agent Configuration
                </h1>
                <p className="text-text-secondary mt-1">Manage system prompts and version history for AI bots.</p>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-0">
                {/* Sidebar: Bot List */}
                <div className="lg:col-span-1 bg-card border border-border rounded-lg shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-border bg-slate-50 dark:bg-slate-900/50">
                        <h2 className="font-bold text-sm uppercase text-text-secondary">Available Agents</h2>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {allData.botConfigs.map(bot => (
                            <button
                                key={bot.id}
                                onClick={() => setSelectedBotId(bot.id)}
                                className={`w-full text-left p-3 rounded-md transition-colors flex items-center gap-3 ${selectedBotId === bot.id
                                    ? 'bg-primary/10 border border-primary/20 text-primary'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-text-primary'
                                    }`}
                            >
                                <Bot size={20} />
                                <div>
                                    <div className="font-semibold text-sm">{bot.name}</div>
                                    <div className="text-xs opacity-70 truncate max-w-[150px]">{bot.description}</div>
                                </div>
                            </button>
                        ))}
                        {allData.botConfigs.length === 0 && (
                            <div className="p-4 text-center text-sm text-text-secondary italic">
                                No bots configured.
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content: Editor & History */}
                <div className="lg:col-span-3 flex flex-col gap-6 overflow-hidden">
                    {selectedBot ? (
                        <>
                            {/* Editor Panel */}
                            <div className="bg-card border border-border rounded-lg shadow-sm flex flex-col flex-1 min-h-[400px]">
                                <div className="p-4 border-b border-border flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                                    <div className="flex items-center gap-2">
                                        <Edit3 size={18} className="text-text-secondary" />
                                        <h2 className="font-bold text-text-primary">System Prompt Editor</h2>
                                        {isDirty && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full border border-yellow-200 ml-2">Unsaved Changes</span>}
                                    </div>
                                    <button
                                        onClick={handleSaveRevision}
                                        disabled={!isDirty}
                                        className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        <Save size={16} /> Save New Version
                                    </button>
                                </div>
                                <div className="flex-1 p-0 relative">
                                    <textarea
                                        value={currentPrompt}
                                        onChange={handlePromptChange}
                                        className="w-full h-full p-6 bg-background text-text-primary font-mono text-sm resize-none focus:outline-none focus:ring-0"
                                        spellCheck={false}
                                    />
                                </div>
                            </div>

                            {/* Revision History */}
                            <div className="bg-card border border-border rounded-lg shadow-sm flex-shrink-0 max-h-60 overflow-hidden flex flex-col">
                                <div className="p-3 border-b border-border bg-slate-50 dark:bg-slate-900/50">
                                    <h3 className="font-bold text-sm flex items-center gap-2 text-text-secondary">
                                        <History size={16} /> Revision History
                                    </h3>
                                </div>
                                <div className="overflow-y-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-background text-xs uppercase text-text-secondary font-semibold sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2">Ver</th>
                                                <th className="px-4 py-2">Date</th>
                                                <th className="px-4 py-2">Author</th>
                                                <th className="px-4 py-2">Status</th>
                                                <th className="px-4 py-2 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {selectedBot.revisions.map(rev => (
                                                <tr key={rev.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                    <td className="px-4 py-2 font-mono text-xs">v{rev.version}</td>
                                                    <td className="px-4 py-2 text-text-secondary text-xs">{new Date(rev.timestamp).toLocaleString()}</td>
                                                    <td className="px-4 py-2">{rev.author}</td>
                                                    <td className="px-4 py-2">
                                                        {rev.id === selectedBot.activeRevisionId ? (
                                                            <span className="flex items-center gap-1 text-green-600 text-xs font-bold bg-green-50 px-2 py-0.5 rounded-full w-fit">
                                                                <CheckCircle size={12} /> Active
                                                            </span>
                                                        ) : (
                                                            <span className="text-text-secondary text-xs">Archived</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        {rev.id !== selectedBot.activeRevisionId && (
                                                            <button
                                                                onClick={() => handleRestoreRevision(rev)}
                                                                className="text-primary hover:text-primary/80 text-xs font-medium flex items-center gap-1 ml-auto"
                                                            >
                                                                <RotateCcw size={12} /> Revert
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center bg-card border border-border rounded-lg text-text-secondary">
                            <Bot size={48} className="mb-4 opacity-20" />
                            <p>Select an agent to configure.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BotConfigPage;
